<!--
* UI Name: HTTP
* Modify Date: 2022/06/15
-->
<template>
  <div>
    <v-form ref="form">
      <v-row>
        <v-col cols="12" class="px-2 py-0">
          <v-row>
            <v-col cols="12" md="12" class="py-0">
              <v-label>
                <span class="field-label required-text">{{ $t('lbl.communications.uri') }}</span>
              </v-label>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12" md="12" class="py-0">
              <span>{{ resourceId }}</span>
              <v-text-field
                v-model="path"
                solo
                hide-details
                @keyup="changePath($event)"
                @change="checkDuplicatedUri()"
                :disabled="!$auth.manage"
                :readonly="isHistory"
              ></v-text-field>
            </v-col>
          </v-row>
          <v-row class="mb-5">
            <v-col cols="12" md="6">
              {{ $t('lbl.communications.httpRecommend') }}
            </v-col>
            <v-col cols="12" md="6">
              {{ $t('lbl.communications.httpEx') }}
            </v-col>
          </v-row>
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="12" class="px-2 py-0">
          <v-row>
            <v-col cols="12" md="12" class="py-0">
              <v-label>
                <v-icon x-small>fas fa-asterisk</v-icon>
                <span class="field-label required-text ml-1">{{
                  $t('lbl.communications.api')
                }}</span>
              </v-label>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12" md="10" class="py-0">
              <v-text-field
                v-model="formData.authToken"
                :rules="[validationUtils.required]"
                solo
                :disabled="!$auth.manage"
              ></v-text-field>
            </v-col>
            <v-col cols="12" md="2" class="py-0">
              <v-btn
                @click="generateApiKey"
                class="btn btn-popup-search btn-window-generate"
                color="primary"
                :disabled="!$auth.manage || !!formData.id"
                :readonly="isHistory"
                :style="this.item.action == 'edit' ? 'pointer-events: none' : ''"
                >{{ $t('btn.communications.generate') }}
              </v-btn>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12" md="12" class="pt-0">
              <v-checkbox
                label="Use JSON Schema Validation"
                true-value="Y"
                false-value="N"
                solo
                hide-details
                v-model="formData.checkJson"
              ></v-checkbox>
            </v-col>
          </v-row>
          <v-row v-if="formData.checkJson == 'Y'">
            <v-col cols="6" md="6" class="pb-0">
              <v-label>JSON Schema</v-label>
            </v-col>
            <v-col cols="6" md="6" class="text-right pb-0" v-html="JsonSchemaCheckResult"> </v-col>
            <v-col cols="12" md="12" no-gutters class="json-schema-right-layer">
              <div class="CodeMirror json-color-format">
                <ScannerViewer
                  v-model="tokenConfigs"
                  :options="this.cmOption"
                  ref="scannerFile"
                ></ScannerViewer>
              </div>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12" md="4" class="px-2 py-0">
              <v-label>{{ $t('lbl.communications.maxRetryAttempts') }}</v-label>
              <v-text-field
                v-model="formData.maxRetry"
                type="number"
                solo
                :disabled="!$auth.manage"
              ></v-text-field>
            </v-col>
          </v-row>
        </v-col>
      </v-row>
    </v-form>
    <dialogWindow
      ref="DuplicatePatternDialog"
      size="max"
      :detail="checkDialogInfo"
      :color="checkDialogColor"
      @select-ok="$refs.DuplicatePatternDialog.dialog = false"
    >
    </dialogWindow>
  </div>
</template>

<script>
import dialogWindow from '@/vue-dlb/components/NewDialog.vue';
import EventBus from '@/vue-caas/views/process/component/event-bus.js';
import config from '@/vue-caas/config/config.js';
import ScannerViewer from '@/vue-dlb/components/viewers/codemirror.vue';
import { getEditData } from '@/vue-caas/utils/process';

// language
import 'codemirror/mode/swift/swift.js';

// theme css
import 'codemirror/theme/rubyblue.css';

// active-line.js
import 'codemirror/addon/selection/active-line.js';

// styleSelectedText
import 'codemirror/addon/selection/mark-selection.js';
import 'codemirror/addon/search/searchcursor.js';

// highlightSelectionMatches
import 'codemirror/addon/scroll/annotatescrollbar.js';
import 'codemirror/addon/search/matchesonscrollbar.js';
import 'codemirror/addon/search/searchcursor.js';
import 'codemirror/addon/search/match-highlighter.js';

// keyMap
import 'codemirror/mode/clike/clike.js';
import 'codemirror/addon/edit/matchbrackets.js';
import 'codemirror/addon/comment/comment.js';
import 'codemirror/addon/dialog/dialog.js';
import 'codemirror/addon/dialog/dialog.css';
import 'codemirror/addon/search/searchcursor.js';
import 'codemirror/addon/search/search.js';
import 'codemirror/keymap/sublime.js';

// foldGutter
import 'codemirror/addon/fold/foldgutter.css';
import 'codemirror/addon/fold/brace-fold.js';
import 'codemirror/addon/fold/comment-fold.js';
import 'codemirror/addon/fold/foldcode.js';
import 'codemirror/addon/fold/foldgutter.js';
import 'codemirror/addon/fold/indent-fold.js';
import 'codemirror/addon/fold/markdown-fold.js';
import 'codemirror/addon/fold/xml-fold.js';

export default {
  components: {
    dialogWindow,
    ScannerViewer,
  },
  props: {
    item: {
      type: Object,
    },
    nodeId: {
      type: String,
    },
    isHistory: {
      default: false,
    },
  },

  data() {
    return {
      formData: {
        host: '',
        authToken: '',
      },
      loadFormData: {},
      formError: {
        name: {
          message: '',
        },
      },
      resourceId: '',
      path: '',
      companyId: '',
      checkDialogColor: 'warning',
      checkDialogInfo: '',
      JsonSchemaCheckResult: '',
      tokenConfigs: '',
    };
  },

  computed: {
    cmOption() {
      if (this.$vuetify.theme.isDark == true) {
        return {
          tabSize: 4,
          lineWrapping: true,
          foldGutter: true,
          styleActiveLine: true,
          lineNumbers: true,
          line: true,
          readOnly: false,
          keyMap: 'sublime',
          mode: 'text/x-swift',
          theme: 'rubyblue',
        };
      }
      return {
        tabSize: 4,
        lineWrapping: true,
        foldGutter: true,
        styleActiveLine: true,
        lineNumbers: true,
        line: true,
        readOnly: false,
        keyMap: 'sublime',
        mode: 'text/x-swift',
        theme: 'default',
      };
    },
    communicationList() {
      return this.$store.getters.communicationSelectBoxList;
    },
  },
  watch: {
    item() {
      this.resetFormData();
    },
  },

  created() {
    if (this.item.action == 'add') {
      this.companyId = $cookies.get('comp-id') + '/';
      this.resourceId = config.proxyApiUrl + '/proxy/links/';
      this.path = this.companyId;
    }
    this.resetFormData();
  },

  methods: {
    checkDataChange() {
      let editedData = getEditData(this.formData, this.loadFormData);
      this.$emit('check-data-change', editedData);
    },
    checkDuplicatedUri() {
      let formDataUri = this.loadFormData.host + this.companyId + this.loadFormData.path;
      this.communicationList.forEach((item) => {
        let uri = item.host + this.companyId + item.path;
        if (uri === this.resourceId && this.resourceId !== formDataUri && item.type === 'HTTP') {
          if (this.item.action == 'add') {
            this.path = this.companyId;
            this.resourceId = config.proxyApiUrl + '/proxy/links/' + this.path;
            this.formData.path = '';
          } else {
            if (!this.loadFormData.path) {
              this.formData.path = '';
              this.path = this.companyId;
              this.resourceId = this.loadFormData.host;
            } else {
              this.path = this.companyId + this.loadFormData.path;
              this.resourceId = this.loadFormData.host + this.path;
              this.formData.path = this.loadFormData.path;
            }
          }
          this.checkDialogInfo = `Duplicate URI`;
          this.$refs.DuplicatePatternDialog.dialog = true;
          return;
        }
      });
    },
    changePath(evt) {
      if (evt.target.value.substring(0, this.companyId.length) === this.companyId) {
        this.resourceId = config.proxyApiUrl + '/proxy/links/' + this.path;
        this.path = evt.target.value;
        this.formData.path = evt.target.value.substring(this.companyId.length);
      } else {
        this.path = this.companyId;
        this.resourceId = config.proxyApiUrl + '/proxy/links/' + this.path;
        this.formData.path = '';
      }
    },

    checkJsonSchema() {
      try {
        const JSON5 = require('json5');
        this.formData.tokenConfigs = JSON5.parse(this.tokenConfigs);
        if (typeof this.formData.tokenConfigs !== 'object') {
          return 'Input must be JSON';
        }
        return 'OK';
      } catch (e) {
        console.log(e);
        let check = e.toString().split('\n');
        let err = check.find((e) => e.indexOf('JSON5') !== -1);
        if (err) {
          return err.replace('SyntaxError: JSON5: i', 'I');
        }
        return 'Invalid';
      }
    },

    async save(item) {
      if (this.formData.checkJson == 'Y') {
        if (!this.tokenConfigs) {
          this.JsonSchemaCheckResult = "<v-label style='color:red'>Please input JSON</v-label>";
          return -1;
        } else {
          let check = this.checkJsonSchema();
          if (check == 'OK') {
            await this.continueSave(item);
          } else {
            this.JsonSchemaCheckResult = "<v-label style='color:red'>" + check + '</v-label>';
            return -1;
          }
        }
      } else {
        await this.continueSave(item);
      }
    },

    async continueSave(item) {
      let validResult = this.$refs.form.validate();
      if (!validResult) return -1;
      this.formData.id = item.id;
      this.formData.type = item.type;
      this.formData.name = item.name;
      this.formData.protocol = item.type;
      let editedData = getEditData(this.formData, this.loadFormData);
      editedData.id = this.formData.id;
      editedData.name = this.formData.name;
      editedData.protocol = this.formData.type;

      if (this.formData.id) {
        await this.$store
          .dispatch('editCommFromProcSetup', this.formData)
          .then(() => {
            this.sendActivate(this.formData.id, 'COM', editedData);
            let formEmit = {
              comId: this.formData.id,
              nodeId: this.nodeId,
              type: 'COM',
            };
            EventBus.$emit('refresh-list-component', formEmit);
          })
          .catch((err) => {
            alert(err);
          });
      } else {
        console.log('this.formData', this.formData);
        await this.$store
          .dispatch('insertCommFromProcSetup', this.formData)
          .then(() => {
            this.formData.id = this.$store.getters.communicationId;
            let formEmit = {
              comId: this.formData.id,
              nodeId: this.nodeId,
              type: 'COM',
            };
            EventBus.$emit('refresh-list-component', formEmit);
          })
          .catch((err) => {
            alert(err);
          });
      }
    },
    resetFormData() {
      this.formData = Object.assign({}, this.getInitData(), this.item);
      this.loadFormData = Object.assign({}, this.getInitData(), this.item);

      if (this.formData.tokenConfigs) {
        this.tokenConfigs = JSON.stringify(this.formData.tokenConfigs, null, 4);
        this.formData.checkJson = 'Y';
      }
      if (!this.formData.id) {
        this.companyId = $cookies.get('comp-id') + '/';
        this.resourceId = config.proxyApiUrl + '/proxy/links/';
        this.path = this.companyId;
      }
      if (this.formData.id) {
        this.companyId = this.formData.comm_comp_id + '/';
        if (!this.formData.path) {
          this.formData.path = '';
          this.path = this.companyId;
          this.resourceId = this.formData.host;
        } else {
          this.path = this.companyId + this.formData.path;
          this.resourceId = this.formData.host + this.path;
        }
      }
      if (this.$refs.form) this.$refs.form.resetValidation();
    },
    async sendActivate(comId, comTp, info) {
      const params = {
        comId: comId,
        comTp: comTp,
        info: info,
      };
      await this.$store.dispatch('updateProcessInfo', params);
    },
    generateURI() {
      let d = new Date().getTime();
      this.resourceId = '/proxy/' + d;
      this.formData.host = this.resourceId;
      this.formData.resourceId = d.toString();
    },
    generateApiKey() {
      let d = new Date().getTime();
      if (window.performance && typeof window.performance.now === 'function') {
        d += performance.now();
      }
      let uuid = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
        let r = (d + Math.random() * 16) % 16 | 0;
        d = Math.floor(d / 16);
        return (c == 'x' ? r : (r & 0x3) | 0x8).toString(16);
      });
      this.formData.authToken = uuid;
    },
    getInitData() {
      return {
        name: '',
        partner: '',
        host: config.proxyApiUrl + '/proxy/links/',
        authType: 'x-api-key',
        authToken: '',
        type: 'HTTP',
        path: '',
      };
    },
    setFieldValidation(name, message) {
      this.formError[name] = {
        message,
      };
    },
  },
};
</script>

<style>
.btn-window-generate {
  width: 100%;
  height: 40px !important;
  margin-top: 6px;
}
.json-schema-right-layer .CodeMirror {
  height: 320px !important;
}
</style>
